import Head from 'next/head';
import Markdown from 'react-markdown';
import rehypeSlug from 'rehype-slug';
import styled from 'styled-components';

import { sneakersRequest } from 'server/services/sneakers/sneakersRequest';
import { pagesContentMap } from 'termsAndPolicy/utils/pagesContentMap';
import { pageNavLinks } from 'termsAndPolicy/utils/pagesNavLinks';
import { TermsAndPolicyNavigation } from 'termsAndPolicy/components/TermsAndPolicyNavigation';
import { variableFont } from 'styles/theme/text';

import { IPage } from 'shared/types/Page';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';

const Pages = ({ pageContent, navLinks }: { pageContent: IPage; navLinks: IPage[] }) => {
  const { pageTitle } = usePageMetadata({
    defaultTitle: `${pageContent?.title}  `,
  });

  return (
    <Pages.Container>
      <Head>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} key="og:title" />
      </Head>
      <TermsAndPolicyNavigation pages={navLinks} />
      <Pages.Content>
        <Markdown rehypePlugins={[rehypeSlug]}>{pageContent?.content}</Markdown>
      </Pages.Content>
    </Pages.Container>
  );
};

const prefixLegalToLink = (link: string): string => {
  if (!link) return link;
  // If link already starts with /legal/, return as is
  if (link.startsWith('/legal/')) return link;
  // If link starts with /, prepend legal
  if (link.startsWith('/')) return `/legal${link}`;
  // Otherwise prepend /legal/
  return `/legal/${link}`;
};

export const getServerSideProps = async ({ req, res, query, locale }) => {
  const { slug }: { slug: string } = query;

  try {
    const pages: IPage[] = await sneakersRequest(req, res).get('api/v1/pages').json();
    const pageMap = pagesContentMap(pages);
    const navLinks: IPage[] = pageNavLinks(pages);

    // Check if page for this slug exists, if so return page content
    if (pageMap[slug]) {
      // Prefix all nav links with /legal/ if not already prefixed
      const prefixedNavLinks = navLinks.map((page) => ({
        ...page,
        slug: prefixLegalToLink(page.slug),
      }));

      // Prefix the page content link with /legal/ if not already prefixed
      const prefixedPageContent = {
        ...pageMap[slug],
        slug: prefixLegalToLink(pageMap[slug].slug),
      };

      return {
        props: {
          ...(await setServerSideTranslations({ locale })),
          pageContent: prefixedPageContent,
          navLinks: prefixedNavLinks,
        },
      };
    }

    return { notFound: true };
  } catch {
    return { notFound: true };
  }
};

Pages.Container = styled.div`
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  color: ${({ theme }) => theme.navigation.textColor};
  display: flex;
  justify-content: flex-start;
  padding: 32px 16px;
  position: relative;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 32px;
    justify-content: center;
  }
`;

Pages.Content = styled.div`
  display: flex;
  flex-direction: column;

  h1 {
    margin-bottom: 32px;
    text-transform: uppercase;
    ${variableFont({ size: '11.5px', height: '16px', fontWeight: '600' })};
  }

  h2,
  h3 {
    margin-bottom: 8px;
    margin-top: 40px;
    ${variableFont({ size: '11.5px', height: '16px', fontWeight: '600' })};
  }

  a {
    text-decoration: underline;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    max-width: 418px;
  }
`;

export default Pages;
